import { React, useEffect, useState, useMemo } from "react";
import { Box, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import EditableTable from "../../../../../../components/Tables/EditableTable";
import { useGeneralContext } from "../GeneralContext";

export function TablesTab({ tab }) {
  const { getAllTabelsFromDB } = useGeneralContext();

  const [columns, setColumns] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Tables");
  const [selectedTable, setSelectedTable] = useState("");

  const [allTables, setAllTables] = useState([]);

  // -------------------------------------------------------
  // CATEGORY CLASSIFICATION FUNCTION
  // -------------------------------------------------------
  const classifyCategory = (tableName) => {
    if (tableName.includes("#")) return "Dimensional Properties";
    if (tableName.startsWith("Est_")) return "Estimation";
    return "Uncategorized";
  };

  // -------------------------------------------------------
  // BUILD CATEGORY LIST (SORTED)
  // -------------------------------------------------------
  const categories = useMemo(() => {
    if (!allTables) return [];

    const setCats = new Set();

    Object.keys(allTables).forEach((name) => {
      setCats.add(classifyCategory(name));
    });

    return ["All Tables", ...Array.from(setCats).sort()];
  }, [allTables]);

  // -------------------------------------------------------
  // FILTER TABLES BY CATEGORY (AND SORT ALPHABETICALLY)
  // -------------------------------------------------------
  const filteredTables = useMemo(() => {
    if (!allTables) return [];

    let tables = [];

    if (selectedCategory === "All Tables") {
      tables = Object.keys(allTables);
    } else {
      tables = Object.keys(allTables).filter(
        (name) => classifyCategory(name) === selectedCategory
      );
    }

    return tables.sort();
  }, [allTables, selectedCategory]);

  // -------------------------------------------------------
  // GET ALL TABLES FROM DB
  // -------------------------------------------------------
  useEffect(() => {
    async function fetchTables() {
      const tables = await getAllTabelsFromDB(tab);
      setAllTables(tables);
      // console.log("All tables fetched for tab", tab, ":", tables);
    }
    fetchTables();
  }, [getAllTabelsFromDB, tab]);
  // -------------------------------------------------------
  // AUTO-SELECT FIRST TABLE OF THE CURRENT CATEGORY
  // -------------------------------------------------------
  useEffect(() => {
    if (filteredTables.length > 0) {
      setSelectedTable(filteredTables[0]);
    } else {
      setSelectedTable("");
    }
  }, [filteredTables]);

  // -------------------------------------------------------
  // BUILD DYNAMIC COLUMNS
  // -------------------------------------------------------
  useEffect(() => {
    if (selectedTable && allTables[selectedTable]?.length) {
      setColumns(
        Object.keys(allTables[selectedTable][0]).map((key) => ({
          accessorFn: (row) => row[key],
          id: key,
          header: key,
        }))
      );
    } else {
      setColumns([]);
    }
  }, [selectedTable, allTables]);

  const tablesApi = {
    getItems: async (parentId) => {
      // console.log("parent", parentId);

      return allTables ? allTables[parentId] : [];
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        pt: 2,
        width: "100%",
      }}
    >
      {/* CATEGORY + TABLE SELECTORS */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          px: 1, // ✅ OFFSET FROM BORDERS
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Select Table</InputLabel>
          <Select
            label="Select Table"
            value={selectedTable}
            onChange={(e) => setSelectedTable(e.target.value)}
          >
            {filteredTables.map((table) => (
              <MenuItem key={table} value={table}>
                {table}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* TABLE WITH GUARANTEED HORIZONTAL SCROLL */}
      <Box
        sx={{
          width: "100%",
          overflowX: "auto", // ✅ ENABLE SCROLL
          overflowY: "hidden",
        }}
      >
        <Box
          sx={{
            minWidth: "1400px", // ✅ FORCE SCROLL (adjust as needed)
          }}
        >
          <EditableTable
            name={selectedTable}
            columns={columns}
            displayMode="modal"
            api={tablesApi}
            enableEditing={false}
            enableCreate={false}
            initialData={{}}
            parentId={selectedTable}
          />
        </Box>
      </Box>
    </Box>
  );

  // return (
  //   <Box
  //     sx={{
  //       backgroundColor: "white",
  //       display: "flex",
  //       flexDirection: "column",
  //       alignItems: "center",
  //       pt: 2,
  //       width: "100%",
  //     }}
  //   >
  //     {/* CATEGORY + TABLE SELECTORS */}
  //     <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
  //       {/* CATEGORY SELECT */}
  //       <FormControl size="small" sx={{ minWidth: 200 }}>
  //         <InputLabel>Category</InputLabel>
  //         <Select
  //           label="Category"
  //           value={selectedCategory}
  //           onChange={(e) => setSelectedCategory(e.target.value)}
  //         >
  //           {categories.map((cat) => (
  //             <MenuItem key={cat} value={cat}>
  //               {cat}
  //             </MenuItem>
  //           ))}
  //         </Select>
  //       </FormControl>

  //       {/* TABLE SELECT */}
  //       <FormControl size="small" sx={{ minWidth: 200 }}>
  //         <InputLabel>Select Table</InputLabel>
  //         <Select
  //           value={selectedTable}
  //           label="Select Table"
  //           onChange={(e) => setSelectedTable(e.target.value)}
  //         >
  //           {filteredTables.map((table) => (
  //             <MenuItem key={table} value={table}>
  //               {table}
  //             </MenuItem>
  //           ))}
  //         </Select>
  //       </FormControl>
  //     </Box>

  //     {/* TABLE DISPLAY */}
  //     <Box width="100%">
  //       <EditableTable
  //         name={selectedTable}
  //         columns={columns}
  //         displayMode="modal"
  //         api={tablesApi}
  //         enableEditing={false}
  //         enableCreate={false}
  //         initialData={{}}
  //         parentId={selectedTable}
  //       />
  //     </Box>
  //   </Box>
  // );
}
